import { Link } from "react-router-dom";
import { ArrowRight, Plane, Radar, ShieldCheck, FileBarChart } from "lucide-react";
import { Reveal, MaskLines, SectionHead, HUDFrame, Counter } from "@/components/anim";
import { IMAGES, DAAS_STEPS } from "@/lib/content";

const INCLUDED = [
  { icon: Plane, title: "Purpose-built drone", desc: "Mission-matched airframe from our own fleet — sprayer, mapper or scout." },
  { icon: Radar, title: "Certified pilot", desc: "Trained remote pilot deployed to your site with the aircraft." },
  { icon: ShieldCheck, title: "Compliance handled", desc: "Mission scoping and regulatory alignment built into every booking." },
  { icon: FileBarChart, title: "Data + insight", desc: "Every flight ends in a decision-ready deliverable, not just footage." },
];

const MODELS = [
  { unit: "PER ACRE", use: "Agriculture spraying", testid: "daas-model-acre" },
  { unit: "PER HOUR", use: "Surveillance & inspection", testid: "daas-model-hour" },
  { unit: "PER PROJECT", use: "Mapping & survey", testid: "daas-model-project" },
];

export default function Services() {
  return (
    <main>
      <section className="relative overflow-hidden hud-grid noise pt-36 md:pt-44 pb-20 md:pb-28">
        <div className="pointer-events-none absolute -top-32 right-0 h-[440px] w-[440px] rounded-full bg-kinetic/10 blur-[130px]" />
        <div className="relative mx-auto grid max-w-7xl gap-12 px-5 md:px-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
          <div>
            <MaskLines
              lines={[
                <span key="e" className="font-hud text-[11px] md:text-xs uppercase tracking-[0.3em] text-kinetic">
                  [ DRONE-AS-A-SERVICE ]
                </span>,
              ]}
            />
            <MaskLines
              className="mt-6 font-display font-extrabold tracking-[-0.02em] leading-[0.98] text-[clamp(2.4rem,5.6vw,5.2rem)]"
              lines={["PAY ONLY", <span key="2">WHEN YOU <span className="text-kinetic">FLY.</span></span>]}
              delay={0.2}
            />
            <MaskLines
              className="mt-7 max-w-xl text-base md:text-lg text-[#9CA3AF] leading-relaxed"
              lines={[
                <span key="s" className="block">
                  Drone capability on demand — without owning aircraft, hiring pilots or managing compliance.
                  Zero hardware burden, full mission output.
                </span>,
              ]}
              delay={0.55}
            />
            <MaskLines
              className="mt-9"
              lines={[
                <Link
                  key="c"
                  to="/contact"
                  data-testid="daas-cta-book-mission"
                  className="group inline-flex items-center gap-3 rounded-full bg-kinetic px-7 py-4 font-hud text-xs font-semibold uppercase tracking-[0.18em] text-void transition-all duration-300 hover:shadow-[0_0_44px_rgba(255,122,26,0.5)] hover:-translate-y-0.5"
                >
                  Book a mission
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                </Link>,
              ]}
              delay={0.75}
            />
          </div>
          <Reveal delay={0.3}>
            <HUDFrame testid="daas-hero-image-frame">
              <div className="overflow-hidden">
                <img src={IMAGES.controller} alt="Pilot commanding an Aeris drone" className="h-[320px] md:h-[440px] w-full object-cover img-treat" />
                <div className="absolute inset-0 bg-gradient-to-t from-void/70 via-transparent to-void/10" />
              </div>
            </HUDFrame>
          </Reveal>
        </div>
      </section>

      <section className="border-y border-white/10 bg-surface py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <SectionHead
            sec="SERVICE BLUEPRINT"
            eyebrow="Aeris Mission Console"
            title="Four steps from scope to insight."
          />
          <div className="mt-14 grid gap-6 md:grid-cols-4">
            {DAAS_STEPS.map((s, i) => (
              <Reveal key={s.step} delay={i * 0.09} className="h-full">
                <div className="relative h-full rounded-2xl border border-white/10 bg-panel/50 p-7 transition-all duration-300 hover:border-kinetic/50" data-testid={`daas-step-${s.step.toLowerCase()}`}>
                  <span className="font-display text-5xl font-extrabold text-kinetic/20">{`0${i + 1}`}</span>
                  <h3 className="mt-10 font-display text-xl font-bold tracking-tight text-kinetic">{s.step}</h3>
                  <p className="mt-2 text-sm text-[#D1D5DB]">{s.title}</p>
                  {i < 3 && (
                    <ArrowRight className="absolute -right-4 top-1/2 hidden h-5 w-5 -translate-y-1/2 text-kinetic/50 md:block" />
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-5 md:px-8 grid gap-14 lg:grid-cols-2">
          <div>
            <SectionHead sec="WHAT'S INCLUDED" eyebrow="Every booking, end to end" title="One crew. Everything covered." />
            <div className="mt-10 space-y-5">
              {INCLUDED.map((it, i) => (
                <Reveal key={it.title} delay={i * 0.07}>
                  <div className="flex gap-5 rounded-xl border border-white/10 bg-panel/40 p-5" data-testid={`daas-included-${i + 1}`}>
                    <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-kinetic/30 bg-kinetic/10 text-kinetic">
                      <it.icon className="h-5 w-5" />
                    </span>
                    <div>
                      <h3 className="font-display font-bold">{it.title}</h3>
                      <p className="mt-1 text-sm text-[#9CA3AF]">{it.desc}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <div>
            <SectionHead sec="ENGAGEMENT MODELS" eyebrow="Pay-per-use" title="Billed the way you work." />
            <div className="mt-10 space-y-5">
              {MODELS.map((m, i) => (
                <Reveal key={m.unit} delay={i * 0.07}>
                  <div className="group flex items-center justify-between rounded-xl border border-white/10 bg-panel/40 p-6 transition-all duration-300 hover:border-kinetic/50 hover:pl-8" data-testid={m.testid}>
                    <div>
                      <p className="font-display text-2xl font-extrabold tracking-tight text-kinetic">{m.unit}</p>
                      <p className="mt-1 text-sm text-[#9CA3AF]">{m.use}</p>
                    </div>
                    <ArrowRight className="h-5 w-5 text-kinetic/50 transition-all duration-300 group-hover:translate-x-1 group-hover:text-kinetic" />
                  </div>
                </Reveal>
              ))}
              <Reveal delay={0.25}>
                <div className="rounded-xl border border-kinetic/25 bg-kinetic/5 p-6 glow-cyan" data-testid="daas-flex-note">
                  <p className="font-display text-lg font-semibold">Pay only when you fly.</p>
                  <p className="mt-2 text-sm text-[#9CA3AF]">
                    DAAS income is earned by acre, hour or project — you carry zero hardware burden and
                    pay strictly for missions flown.
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
